import { useAppStore } from '@/store/store';
import { selectShipmentSummary } from '@/store/selectors';
import { CopyButton } from '@/components/shared/CopyButton';
import { DualUnitDisplay } from '@/components/Calculator/DualUnitDisplay';
import { Tooltip } from '@/components/shared/Tooltip';
import { EmptyState } from '@/components/shared/EmptyState';
import { DimensionUnit, WeightUnit } from '@freight-cbm/shared-types';
import { calcRevenueTon } from '@freight-cbm/shared-logic';
import { Calculator as CalculatorIcon } from 'lucide-react';

export function SummaryPanel() {
  const summary = useAppStore(selectShipmentSummary);
  const mode = useAppStore((s) => s.mode);
  const dimUnit: DimensionUnit = useAppStore((s) => s.packages[0]?.dimUnit ?? DimensionUnit.Cm);
  const weightUnit: WeightUnit = useAppStore((s) => s.packages[0]?.weightUnit ?? WeightUnit.Kg);

  return (
    <div className="card" data-testid="summary-panel">
      <h3 className="section-title">Shipment Summary</h3>
      <div className="metric-grid">
        {summary.totalPackages === 0 ? (
          <EmptyState
            icon={CalculatorIcon}
            compact
            title="Your shipment summary appears here"
            description="Add at least one package with dimensions and weight to see total CBM, chargeable weight, density, and revenue ton."
          />
        ) : (
          <>
            <div className="metric-box metric-box-hero" data-testid="metric-total-cbm">
              <Tooltip content="Cubic meters — the total volume of all packages combined.">
                <span className="metric-label" style={{ cursor: 'help' }}>
                  Total CBM
                </span>
              </Tooltip>
              <span className="metric-value">{summary.totalCBM.toFixed(4)}</span>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
                <span className="metric-unit" style={{ fontSize: 'var(--font-size-sm)' }}>
                  {summary.totalCBM > 0 ? <DualUnitDisplay type="dimension" value={summary.totalCBM} unit={dimUnit} /> : null}
                </span>
                <CopyButton value={String(summary.totalCBM.toFixed(4))} label="Copy CBM" />
              </div>
            </div>
            <MetricBox label="Gross Weight" value={`${summary.grossWeightKg.toFixed(2)} kg`} unit={summary.grossWeightKg > 0 ? <DualUnitDisplay type="weight" value={summary.grossWeightKg} unit={weightUnit} /> : undefined} />
            <MetricBox label="Total Packages" value={String(summary.totalPackages)} />
            <MetricBox label="Chargeable Weight" helpText="The greater of actual weight and dimensional weight — this is what carriers bill on." value={`${summary.chargeableWeightKg.toFixed(2)} kg`} unit={summary.chargeableWeightKg > 0 ? <DualUnitDisplay type="weight" value={summary.chargeableWeightKg} unit={weightUnit} /> : undefined} emphasize />
            {mode === 'ocean-lcl' && (
              <MetricBox label="Revenue Ton" helpText="The greater of volume (CBM) or weight (metric tons), used as the billing unit for ocean LCL." value={`${calcRevenueTon(summary.totalCBM, summary.grossWeightKg).revenueTon.toFixed(4)}`} emphasize />
            )}
            <MetricBox label="Cargo Density" helpText="Shipment density in kg/m³. Below the break-even density, charges are volume-driven." value={`${summary.cargoDensityKgPerCBM.toFixed(2)} kg/m³`} />
          </>
        )}
      </div>
    </div>
  );
}

function MetricBox({ label, value, unit, helpText, emphasize }: { label: string; value: string; unit?: React.ReactNode; helpText?: string; emphasize?: boolean }) {
  const labelContent = (
    <span className="metric-label" style={{ cursor: helpText ? 'help' : undefined }}>
      {label}
    </span>
  );

  return (
    <div className={`metric-box${emphasize ? ' metric-box-emphasize' : ''}`}>
      {helpText ? <Tooltip content={helpText}>{labelContent}</Tooltip> : labelContent}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span className="metric-value" style={emphasize ? { color: 'var(--color-accent)' } : undefined}>
          {value}
        </span>
        <CopyButton value={value} label="Copy" />
      </div>
      {unit && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {unit}
        </div>
      )}
    </div>
  );
}
